import { executeRequest } from '@/core/either';
import { http, httpPublic } from '@/core/api';
import type { ApiResult } from '@/core/either';
import type { RegisterUserType } from '@/types/AuthType';
import type { TypeCountryTerms, TypeShippingRoutes } from '@/types/TermsConditions';
import type { RouteTransfer } from '@/types/RouteTransferType';
import type { OnboardingCountryType } from '@/types/AuthType';

import { updateTokens } from '@/shared/common';

import type {
  RecoverPasswordAuthorizationParams,
  RecoverPasswordAuthorizationResponse,
  RecoverPasswordParams,
  UpdatePasswordParams,
  ConfirmEmailParams,
  ManualCodeConfirmParams,
  LoginMeta,
  SignUpMeta,
  ExtOtpSendParams,
  ExtOtpValidateParams,
  ExtOtpValidateResponse,
  CompanyDocumentType,
  LoginOtpType,
  LoginInterface,
} from '../types/authentication.type';

/**
 * Servicio encargado de la gestión de autenticación y registro de usuarios.
 * Proporciona métodos para login, registro, recuperación de contraseña y confirmación de email.
 */
export const authenticationService = {
  /**
   * Inicia sesión con OTP o Password usando credenciales.
   * @param data - Objeto con credenciales y datos de recaptcha.
   * @param meta - Objeto con información de fingerprint y dispositivo.
   * @returns Promesa con el resultado tipado usando Either (ApiResult).
   */
  login(data: LoginOtpType, meta: LoginMeta): Promise<ApiResult<LoginInterface>> {
    return executeRequest(async () => {
      const response = await httpPublic.post<LoginInterface>(
        'auth-server/b2b/auth/login',
        {
          email: data.email,
          password: data.password,
          recaptchaInfo: data.recaptchaInfo,
          ...(data?.useOtp && {
            useOtp: data?.useOtp,
          }),
        },
        {
          headers: {
            fingerprint: meta.fingerprint,
            authType: data?.authType || 'WHATSAPP',
            authValue: data?.authValue || '',
            ...meta.deviceInfo,
          },
        }
      );

      if (response.data?.idToken) {
        updateTokens({
          idToken: response.data.idToken,
          refreshToken: response.data.refreshToken ?? undefined,
          expiresIn: response.data.expiresIn ?? undefined,
        });
      }

      return response;
    });
  },

  /**
   * Inicia sesión usando el endpoint v2 de autenticación.
   * Soporta el flujo con selector de canal OTP cuando el teléfono está validado.
   * @param data - Objeto con credenciales, tipo de autenticación y datos de recaptcha.
   * @param meta - Objeto con información de fingerprint y dispositivo.
   * @returns Promesa con el resultado tipado usando Either (ApiResult).
   */
  loginV2(data: LoginOtpType, meta: LoginMeta): Promise<ApiResult<LoginInterface>> {
    return executeRequest(async () => {
      const headers: Record<string, string> = {
        fingerprint: meta.fingerprint,
        'X-API-VERSION': '2',
        ...meta.deviceInfo,
      };

      if (data.authType) headers.authType = data.authType;
      if (data.authValue) headers.authValue = data.authValue;

      const response = await httpPublic.post<LoginInterface>(
        'auth-server/b2b/auth/login',
        {
          email: data.email,
          password: data.password,
          recaptchaInfo: data.recaptchaInfo,
          ...(data?.useOtp && {
            useOtp: data?.useOtp,
          }),
        },
        { headers }
      );

      if (
        response.data?.idToken &&
        data?.authValue !== null &&
        data?.authValue !== undefined
      ) {
        updateTokens({
          idToken: response.data.idToken,
          refreshToken: response.data.refreshToken ?? undefined,
          expiresIn: response.data.expiresIn ?? undefined,
        });
      }

      return response;
    });
  },


  /**
   * Registra una nueva empresa en la plataforma.
   * @param authData - Datos de registro de la empresa y usuario.
   * @param meta - Objeto con información de fingerprint.
   * @returns Promesa con el resultado tipado usando Either (ApiResult).
   */
  signUp(authData: RegisterUserType, meta: SignUpMeta): Promise<ApiResult<void>> {
    return executeRequest(async () => {
      return httpPublic.post('auth-server/b2b/auth/signup', authData, {
        headers: {
          fingerPrint: meta.fingerprint,
        },
      });
    });
  },

  /**
   * Solicita el envío de un correo de recuperación de contraseña.
   * @param params - Objeto con email y token de reCAPTCHA v3.
   * @returns Promesa con el resultado tipado usando Either (ApiResult).
   */
  recoverPassword(params: RecoverPasswordParams): Promise<ApiResult<void>> {
    return executeRequest(() =>
      httpPublic.post('company/ext/password/forgot', {
        email: params.email,
        token: params.token,
      })
    );
  },

  /**
   * Consulta si el usuario puede continuar al selector de métodos de recuperación.
   * @param params - Objeto con email y token de reCAPTCHA v3.
   * @returns Promesa con el resultado tipado usando Either (ApiResult).
   */
  getRecoverPasswordAuthorization(
    params: RecoverPasswordAuthorizationParams
  ): Promise<ApiResult<RecoverPasswordAuthorizationResponse>> {
    return executeRequest(() =>
      httpPublic.post<RecoverPasswordAuthorizationResponse>(
        'company/ext/users/authorization-checks',
        {
          email: params.email,
          token: params.token,
        }
      )
    );
  },

  /**
   * Envía un código OTP al canal indicado (SMS, WHATSAPP o EMAIL) mediante el flujo externo.
   * @param params - Parámetros con canal, destinatario, email y token de reCAPTCHA v3.
   * @returns Promesa con el resultado tipado usando Either (ApiResult).
   */
  createExtOtp(params: ExtOtpSendParams): Promise<ApiResult<void>> {
    return executeRequest(() =>
      httpPublic.post('auth-server/ext/otp/send', params)
    );
  },

  /**
   * Valida el código OTP externo ingresado por el usuario.
   * @param params - Parámetros con email, código y token de reCAPTCHA v3.
   * @returns Promesa con el resultado tipado usando Either (ApiResult<ExtOtpValidateResponse>).
   */
  verifyExtOtp(params: ExtOtpValidateParams): Promise<ApiResult<ExtOtpValidateResponse>> {
    return executeRequest(() =>
      httpPublic.post<ExtOtpValidateResponse>('auth-server/ext/otp/validate', params)
    );
  },

  /**
   * Actualiza la contraseña del usuario mediante el flujo de olvido.
   * @param params - Objeto con email, password, passwordConfirmation, code y token de reCAPTCHA v3.
   * @returns Promesa con el resultado tipado usando Either (ApiResult).
   */
  updatePassword(params: UpdatePasswordParams): Promise<ApiResult<void>> {
    return executeRequest(() =>
      httpPublic.post('company/ext/password/forgot/confirm', {
        email: params.email,
        password: params.password,
        confirmPassword: params.passwordConfirmation,
        code: params.code,
        token: params.token,
        ...(params.flow && { flow: params.flow }),
      })
    );
  },

  /**
   * Confirma el correo electrónico del usuario.
   * @param params - Objeto con email, code y type.
   * @returns Promesa con el resultado tipado usando Either (ApiResult).
   */
  confirmEmail(params: ConfirmEmailParams): Promise<ApiResult<void>> {
    return executeRequest(() => 
      httpPublic.post('company/ext/onboarding/sing-up/confirm', {
        email: params.email,
        code: params.code,
        type: params.type,
      })
    );
  },

  /**
   * Activa la cuenta con un código de activación entregado por el ejecutivo de cuenta.
   * @param params - Objeto con email y code.
   * @returns Promesa con el resultado tipado usando Either (ApiResult).
   */
  createManualCodeConfirmation(params: ManualCodeConfirmParams): Promise<ApiResult<void>> {
    return executeRequest(() =>
      httpPublic.post('company/ext/onboarding/sing-up/confirm/manual', {
        email: params.email,
        code: params.code,
      })
    );
  },

  /**
   * Reenvía el correo de confirmación de registro.
   * @param email - Correo electrónico del usuario.
   * @returns Promesa con el resultado tipado usando Either (ApiResult).
   */
  resendEmailConfirmation(email: string): Promise<ApiResult<void>> {
    return executeRequest(() => 
      httpPublic.post(`company/ext/onboarding/sing-up/resend`, { email })
    );
  },

  /**
   * Obtiene los términos y condiciones de un país específico.
   * @param routes - Rutas de envío para filtrar términos.
   * @returns Promesa con el resultado tipado usando Either (ApiResult).
   */
  getCountryTerms(routes: TypeShippingRoutes): Promise<ApiResult<TypeCountryTerms[]>> {
    return executeRequest(() => 
      http.post<TypeCountryTerms[]>('route/b2b/customers/acknowledgment', routes)
    );
  },

  /**
   * Obtiene las rutas externas disponibles.
   * @returns Promesa con el resultado tipado usando Either (ApiResult).
   */
  getExternalRoutes(): Promise<ApiResult<RouteTransfer[]>> {
    return executeRequest(() => 
      http.get<RouteTransfer[]>('quote/ext/routes')
    );
  },

  /**
   * Obtiene la lista de países disponibles para el onboarding público.
   * @returns Promesa con el resultado tipado usando Either (ApiResult).
   */
  getAvailableOnboardingCountries(): Promise<ApiResult<OnboardingCountryType[]>> {
    return executeRequest(() =>
      httpPublic.get<OnboardingCountryType[]>('company/ext/onboarding/sing-up/countries')
    );
  },

  /**
   * Obtiene los tipos de documento de empresa disponibles para un país de constitución.
   * @param countryCode - Código ISO Alpha-2 del país de constitución.
   * @returns Promesa con el resultado tipado usando Either (ApiResult).
   */
  getCompanyDocumentTypes(countryCode: string): Promise<ApiResult<CompanyDocumentType[]>> {
    return executeRequest(() =>
      httpPublic.get<CompanyDocumentType[]>(
        'company/ext/onboarding/sing-up/company-document-types',
        { params: { countryCode } }
      )
    );
  },
};
